import React, { useEffect, useMemo, useState } from 'react';

// Drill-in detail for one past (or currently running) ingest job — lazy-loads
// the full per-file manifest from disk (_ingest/manifest_*.json, already
// written by startOffload) only when expanded, rather than every job history
// entry carrying its full file list in memory all the time. Polls while the
// job is still running so failures/completions show up live, not just once
// the job finishes.
export default function JobDetailView({ c, job }) {
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadManifest = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await window.electronAPI.cardIngest.getJobManifest(job.jobId);
        if (cancelled) return;
        if (result?.success) setManifest(result.data);
        else setError(result?.error || 'Manifest not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadManifest();
    const timer = job.status === 'running' ? setInterval(loadManifest, 1500) : null;
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [job.jobId, job.destPath, job.status]);

  const failedFiles = useMemo(
    () => (manifest?.files || []).filter(f => !f.verified && !f.pending),
    [manifest],
  );

  return (
    <div style={{ borderTop: `1px solid ${c.rule}`, padding: '10px 12px', background: c.subtleBg }}>
      {loading && <div style={{ color: c.muted }}>Loading manifest…</div>}
      {error && <div style={{ color: c.muted }}>Per-file manifest unavailable ({error}) — the job summary above is all that's kept for this ingest.</div>}

      {manifest && (
        <>
          {failedFiles.length > 0 && (
            <div style={{ marginBottom: '8px' }}>
              <div style={{ fontWeight: 600, color: c.err, marginBottom: '4px' }}>
                {failedFiles.length} file{failedFiles.length === 1 ? '' : 's'} failed verification
              </div>
              {failedFiles.map(f => (
                <div key={f.relativePath} style={{ color: c.err, fontFamily: 'monospace', marginBottom: '2px' }}>
                  {f.relativePath} — {f.error}
                </div>
              ))}
            </div>
          )}
          {manifest.remoteUploaded !== null && manifest.remoteUploaded !== undefined && (
            <div style={{ color: c.muted, marginBottom: '8px' }}>
              {manifest.remoteUploaded}/{manifest.fileCount} also uploaded to remote server
            </div>
          )}
          <div style={{ fontWeight: 600, color: c.text, marginBottom: '4px' }}>
            All files ({manifest.files.length})
          </div>
          <div style={{ maxHeight: '220px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {manifest.files.map(f => (
              <div key={f.relativePath} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', fontFamily: 'monospace' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: f.pending ? c.muted : (f.verified ? c.muted : c.err) }}>{f.relativePath}</span>
                <span style={{ flexShrink: 0, color: f.pending ? c.muted : (f.verified ? c.ok : c.err) }}>
                  {f.pending
                    ? (job.status === 'running' ? 'not reached yet' : 'pending when interrupted')
                    : (f.verified ? 'verified' : 'failed')}{f.uploaded ? ' · uploaded' : ''}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
